import React from 'react';
import { Link } from 'react-router-dom';

const COLORS = { black: '#0A0A0A', white: '#F5F5F0', gold: '#C9A84C', gray: '#888' };

function FeatureBlock({ icon, title, body }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      padding: '2.5rem',
      border: `1px solid rgba(255,255,255,0.08)`,
    }}>
      <span style={{ fontSize: '2rem' }}>{icon}</span>
      <h3 style={{
        fontSize: '1.1rem',
        fontWeight: 800,
        letterSpacing: '0.12em',
        color: COLORS.white,
        margin: 0,
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '0.9rem',
        lineHeight: 1.7,
        color: COLORS.gray,
        margin: 0,
        fontFamily: 'Georgia, serif',
        fontStyle: 'italic',
      }}>
        {body}
      </p>
    </div>
  );
}

function StepBlock({ number, title, body }) {
  return (
    <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
      <div style={{
        flexShrink: 0,
        width: '48px',
        height: '48px',
        border: `1px solid ${COLORS.gold}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.85rem',
        fontWeight: 900,
        color: COLORS.gold,
        letterSpacing: '0.05em',
      }}>
        {String(number).padStart(2, '0')}
      </div>
      <div>
        <h4 style={{
          margin: '0 0 0.4rem',
          fontSize: '0.9rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          color: COLORS.white,
        }}>
          {title}
        </h4>
        <p style={{
          margin: 0,
          fontSize: '0.88rem',
          color: COLORS.gray,
          lineHeight: 1.6,
        }}>
          {body}
        </p>
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section style={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4rem 2rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Subtle grid background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(201,168,76,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }} />

        <p style={{
          fontSize: '0.7rem',
          letterSpacing: '0.3em',
          color: COLORS.gold,
          marginBottom: '2rem',
          fontWeight: 600,
        }}>
          FASHION-TECH / QR APPAREL / WEARABLE LINKS
        </p>

        <h1 style={{
          fontSize: 'clamp(3rem, 10vw, 9rem)',
          fontWeight: 900,
          lineHeight: 0.9,
          letterSpacing: '-0.02em',
          color: COLORS.white,
          margin: '0 0 2rem',
          maxWidth: '900px',
        }}>
          YOUR LINK.{' '}
          <span style={{ color: COLORS.gold }}>YOUR LOOK.</span>
        </h1>

        <p style={{
          fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
          maxWidth: '540px',
          lineHeight: 1.7,
          color: COLORS.gray,
          marginBottom: '3rem',
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
        }}>
          Every QRQY piece is a living QR code — scan it, own it, redirect it anywhere.
        </p>

        <Link
          to="/shop"
          style={{
            display: 'inline-block',
            backgroundColor: COLORS.gold,
            color: COLORS.black,
            padding: '1rem 3rem',
            fontSize: '0.8rem',
            fontWeight: 800,
            letterSpacing: '0.2em',
            textDecoration: 'none',
            transition: 'background-color 0.15s',
          }}
        >
          SHOP THE DROP
        </Link>
      </section>

      {/* Feature grid */}
      <section style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '6rem 2rem',
      }}>
        <p style={{
          fontSize: '0.7rem',
          letterSpacing: '0.25em',
          color: COLORS.gold,
          marginBottom: '3rem',
          fontWeight: 600,
        }}>
          THE CONCEPT
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1px',
          backgroundColor: 'rgba(255,255,255,0.08)',
        }}>
          <FeatureBlock
            icon="◼"
            title="WEAR IT"
            body="A QR code that belongs on a garment, not a receipt. High-contrast, error-corrected, designed to survive a wash cycle and still scan perfectly."
          />
          <FeatureBlock
            icon="⬡"
            title="OWN IT"
            body="Your account is your ownership record. The moment your order ships, your code is yours — for as long as you want it. Non-transferable. Permanent."
          />
          <FeatureBlock
            icon="→"
            title="CHANGE IT"
            body="Your drop link. Your playlist. Your portfolio. Your moment. Log into the dashboard and redirect your QR anywhere — instantly, whenever you want."
          />
        </div>
      </section>

      {/* How it works */}
      <section style={{
        backgroundColor: 'rgba(255,255,255,0.02)',
        borderTop: `1px solid rgba(255,255,255,0.06)`,
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '6rem 2rem',
        }}>
          <p style={{
            fontSize: '0.7rem',
            letterSpacing: '0.25em',
            color: COLORS.gold,
            marginBottom: '3rem',
            fontWeight: 600,
          }}>
            HOW IT WORKS
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
            <StepBlock
              number={1}
              title="BUY"
              body="Choose your piece from the drop. At checkout, create your QRQY account — this is how you claim the code that comes printed on your garment."
            />
            <StepBlock
              number={2}
              title="CLAIM"
              body="Your order comes with a unique code. Log in to your dashboard, enter your claim code, and the QR is yours. No middlemen. No third parties. Yours."
            />
            <StepBlock
              number={3}
              title="CONTROL"
              body="From the dashboard, set your destination URL. Drop a new track? Change it in 30 seconds. New project? New link. The garment stays the same — the link is alive."
            />
          </div>
        </div>
      </section>

      {/* Editorial banner */}
      <section style={{
        padding: '8rem 2rem',
        textAlign: 'center',
        maxWidth: '900px',
        margin: '0 auto',
      }}>
        <blockquote style={{
          fontSize: 'clamp(1.8rem, 5vw, 4rem)',
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
          fontWeight: 400,
          lineHeight: 1.3,
          color: COLORS.white,
          margin: 0,
          borderLeft: 'none',
        }}>
          "Scan is the new signature."
        </blockquote>
        <div style={{
          marginTop: '2rem',
          height: '1px',
          width: '60px',
          backgroundColor: COLORS.gold,
          margin: '2rem auto 0',
        }} />
        <p style={{
          marginTop: '1.5rem',
          fontSize: '0.7rem',
          letterSpacing: '0.2em',
          color: COLORS.gold,
          fontWeight: 600,
        }}>
          QRQY
        </p>
      </section>
    </div>
  );
}
